/**
 * A group inside a page list that folds under a quiet header: a chevron, the
 * label and its count. Spec beside it in list-section.md.
 *
 * @module components/ui/list-section
 */
import { ChevronRightIcon } from "lucide-react";
import { useState, type ComponentProps, type ReactNode } from "react";

import { cn } from "~/lib/utils";
import { TIMELINE_DISCLOSURE_PANEL_CLASS } from "../chat/timelineDisclosure";
import { Collapsible, CollapsiblePanel, CollapsibleTrigger } from "./collapsible";
import { ListRowIcon } from "./list-row";

/** The chevron a list's folding header leads with; points down while open. */
export function ListSectionChevron({ open }: { readonly open: boolean }) {
  return <ChevronRightIcon aria-hidden className={cn("transition-transform", open && "rotate-90")} />;
}

/** The folded content, indented so its rows' icons sit under the header's label. */
/** The panel's start indent, so its rows' icons sit under the header's label. */
const PANEL_INDENT = { 1: "ms-[22px]", 2: "ms-[44px]" } as const;

export function ListSectionPanel({
  className,
  headerIcons = 1,
  children,
}: {
  readonly className?: string;
  /** Icons before the header's label: the chevron alone, or the chevron and the item's own icon. */
  readonly headerIcons?: keyof typeof PANEL_INDENT;
  readonly children: ReactNode;
}) {
  return (
    <CollapsiblePanel className={TIMELINE_DISCLOSURE_PANEL_CLASS}>
      <div data-slot="list-section-panel" className={cn(PANEL_INDENT[headerIcons], "pb-1.5", className)}>
        {children}
      </div>
    </CollapsiblePanel>
  );
}

export function ListSection({
  label,
  count,
  urgent = false,
  defaultOpen = true,
  children,
}: {
  readonly label: string;
  readonly count: number;
  /** The label in the destructive ink, e.g. Overdue. */
  readonly urgent?: boolean;
  readonly defaultOpen?: boolean;
  readonly children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Collapsible open={open} onOpenChange={setOpen} data-slot="list-section">
      <ListSectionHeader>
        <ListRowIcon>
          <ListSectionChevron open={open} />
        </ListRowIcon>
        <span className={cn("min-w-0 truncate", urgent && "text-destructive")}>{label}</span>
        <span className="tabular-nums">{count}</span>
      </ListSectionHeader>
      <ListSectionPanel>{children}</ListSectionPanel>
    </Collapsible>
  );
}

function ListSectionHeader({ className, ...props }: ComponentProps<typeof CollapsibleTrigger>) {
  return (
    <CollapsibleTrigger
      data-slot="list-section-header"
      className={cn(
        "flex min-h-7 w-full items-center gap-2 rounded-md px-2 text-left text-xs text-muted-foreground outline-none transition-colors duration-[120ms] hover:bg-accent/70 hover:text-foreground focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/70",
        className,
      )}
      {...props}
    />
  );
}
